/**
 * SAME-CHAIN source-token balance gate (beta bug-1a).
 *
 * Extracted from BridgePage's `opexGate` memo so the money-safety check can be unit-tested
 * DETERMINISTICALLY — the render-level test raced the async balance load (the token balance never
 * settled in CI), so the gate is proven here with direct inputs instead.
 *
 * WHY IT EXISTS: `opexGate` early-returns null for a same-chain route ("no opex floor") and `canSend`
 * never checks the balance, so before this a same-chain RH swap of 1000 USDG on a 5.85 balance left the
 * CTA green and dead-ended at a wallet "insufficient funds" revert. Cross-chain keeps its own opex-aware
 * balance gate; this covers ONLY the same-chain, non-native-source path that never reaches it. No opex on
 * same-chain, so the whole held balance is sendable (maxSendable == balance).
 *
 * Returns an `insufficientToken` opex-gate shape when the typed amount exceeds the held token balance,
 * else null (native source, cross-chain, unknown/loading balance, or amount within balance). Fails OPEN
 * on an unknown balance (null) so a still-loading balance never blocks a legitimate send.
 */
/**
 * #614 / G6 — the ONE "atomic same-chain swap" predicate. TRUE when the two chains are equal AND the
 * provider is not a deposit-address rail. A same-chain `near_1click` order (Tron TRX↔USDT) is a
 * deposit → solver-fill flow with a separate delivery transaction, so every site that used to read
 * "same chain ⇒ one atomic tx" must read this instead.
 *
 * UNKNOWN / absent provider ⇒ atomic (GATE-2 checkpoint, operator decision 2026-09-09, option 1): before
 * #614 no same-chain 1Click order could exist, so every legacy same-chain row IS a deBridge / Jupiter
 * atomic swap by construction, and every writer (backend history, stored orders, this session's completed
 * list) now records the provider. Fail-closed applies to what matters — a row KNOWN to be `near_1click`
 * is never described as atomic (that is what presented a user's own deposit as the delivery,
 * BridgePage 1032/3262). A future writer that omits the provider on a 1Click same-chain row reopens
 * that bug; the tests on the writers pin it.
 */
export function isAtomicSameChain(
  sourceChain: string | null | undefined,
  destChain: string | null | undefined,
  provider: string | null | undefined,
): boolean {
  if (sourceChain == null || destChain == null || sourceChain !== destChain) return false
  return provider !== 'near_1click'
}

export type SameChainShortfall = {
  kind: 'insufficientToken'
  needAmt: number
  minUsd: number
  maxSendable: number
}

export function sameChainTokenBalanceGate(opts: {
  isNativeSrc: boolean
  sameChain: boolean
  /** Held source-token balance as a decimal string, or null while it is still loading/unknown. */
  sourceTokenBalanceStr: string | null
  /** The typed send amount (source-token units) as a string. */
  amount: string
}): SameChainShortfall | null {
  const { isNativeSrc, sameChain, sourceTokenBalanceStr, amount } = opts
  // Native source has its own gas/reserve handling; cross-chain has its own opex-aware balance gate;
  // a null balance is still loading — never block on it (parity with opexGate's `== null` guard).
  if (isNativeSrc || !sameChain || sourceTokenBalanceStr == null) return null
  const input = parseFloat(amount || '0')
  const tokenBal = parseFloat(sourceTokenBalanceStr)
  if (input > 0 && Number.isFinite(tokenBal) && tokenBal < input) {
    return { kind: 'insufficientToken', needAmt: Math.ceil(input), minUsd: 0, maxSendable: Math.max(0, tokenBal) }
  }
  return null
}

/**
 * SAME-CHAIN minimum-amount gate.
 *
 * `opexGate` early-returns null for a same-chain route ("no opex floor"), so before this a same-chain
 * amount below the route's minimum (e.g. a $0.50 same-chain deBridge swap under the $1.25 floor) left the
 * CTA green with only a bare "Amount too low for this route" — no min named, and nothing gated. This is
 * the same-chain counterpart to the cross-chain `belowMinHintUsd`: it NAMES the static per-provider floor
 * the backend now returns (`dexFeeEstimate.minAmountUsd`) and disables the CTA when the input is below it.
 *
 * Returns a `belowMinimum` opex-gate shape (`needAmt: 0` — raising the amount is exactly the fix, so no
 * shortfall figure) when the same-chain input is priced and below the floor, else null. Fails OPEN on any
 * unknown/non-positive input: a missing floor (null/0/undefined) or an unpriced input (null/0) never
 * blocks — the min is a soft product floor, not a safety invariant, so an unknown must not strand a user.
 */
export function sameChainMinGate(opts: {
  sameChain: boolean
  /** Static per-provider floor (USD) from the estimate, or null/undefined when the route has none. */
  minAmountUsd: number | null | undefined
  /** USD value of the typed send amount, or null/undefined while it is unknown/unpriced. */
  inputUsd: number | null | undefined
}): { kind: 'belowMinimum'; needAmt: number; minUsd: number } | null {
  const { sameChain, minAmountUsd, inputUsd } = opts
  if (!sameChain) return null
  if (minAmountUsd == null || !(minAmountUsd > 0)) return null   // no floor on this route → never block
  if (inputUsd == null || !(inputUsd > 0)) return null            // input unknown/zero → fail OPEN
  if (inputUsd < minAmountUsd) return { kind: 'belowMinimum', needAmt: 0, minUsd: minAmountUsd }
  return null
}
